/**
 * Form builder: JS validation; file input label; reCAPTCHA v2 invisible (explicit render + execute, then native form submit).
 */
(function () {
  const BUTTON_LOADING_CLASS = 'button--loading';
  const RECAPTCHA_CLASS = 'g-recaptcha';
  const RECAPTCHA_RESPONSE_NAME = 'g-recaptcha-response';
  const FIELD_ERROR_CLASS = 'form-builder__field--error';
  const FIELD_ERROR_MSG_CLASS = 'form-builder__error-msg';

  let recaptchaOnLoadCallbackCalled = false;
  let recaptchaOnLoadCallbackExposed = false;

  /** @type {Array<{ form: HTMLFormElement, root: HTMLElement, recaptchaEl: HTMLElement, callbackName: string, widgetId?: number }>} */
  const recaptchaInstances = [];

  function getFieldWrapper(input) {
    return input.closest('.form-builder__field');
  }

  function showFieldError(wrapper, msg) {
    if (!wrapper) return;
    wrapper.classList.add(FIELD_ERROR_CLASS);
    let errorEl = wrapper.querySelector('.' + FIELD_ERROR_MSG_CLASS);
    if (!errorEl) {
      errorEl = document.createElement('span');
      errorEl.className = FIELD_ERROR_MSG_CLASS;
      errorEl.setAttribute('aria-live', 'polite');
      wrapper.appendChild(errorEl);
    }
    errorEl.textContent = msg;
  }

  function clearFieldError(wrapper) {
    if (!wrapper) return;
    wrapper.classList.remove(FIELD_ERROR_CLASS);
    const errorEl = wrapper.querySelector('.' + FIELD_ERROR_MSG_CLASS);
    if (errorEl) errorEl.textContent = '';
  }

  function validateField(input, strings) {
    const wrapper = getFieldWrapper(input);
    const type = (input.type || '').toLowerCase();
    const msgRequired = strings.field_required || 'This field is required.';

    if (type === 'radio') {
      if (!input.required) { clearFieldError(wrapper); return true; }
      const form = input.closest('form');
      const group = form
        ? Array.from(form.querySelectorAll(`input[type="radio"][name="${CSS.escape(input.name)}"]`))
        : [input];
      if (group.some((r) => r.checked)) { clearFieldError(wrapper); return true; }
      showFieldError(wrapper, msgRequired);
      return false;
    }

    if (type === 'checkbox') {
      if (input.required && !input.checked) {
        showFieldError(wrapper, msgRequired);
        return false;
      }
      clearFieldError(wrapper);
      return true;
    }

    if (type === 'file') {
      if (input.required && (!input.files || input.files.length === 0)) {
        showFieldError(wrapper, msgRequired);
        return false;
      }
      clearFieldError(wrapper);
      return true;
    }

    const value = (input.value || '').trim();

    if (input.required && value === '') {
      showFieldError(wrapper, msgRequired);
      return false;
    }

    if (type === 'email' && value !== '') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        showFieldError(wrapper, strings.email_invalid || 'Please enter a valid email address.');
        return false;
      }
    }

    clearFieldError(wrapper);
    return true;
  }

  function validateForm(form, strings) {
    const fields = Array.from(
      form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]), textarea, select')
    );
    const radioGroupsSeen = new Set();
    let firstInvalid = null;
    let allValid = true;

    for (const field of fields) {
      if (field.type === 'radio') {
        if (radioGroupsSeen.has(field.name)) continue;
        radioGroupsSeen.add(field.name);
      }
      if (!validateField(field, strings)) {
        allValid = false;
        if (!firstInvalid) firstInvalid = field;
      }
    }

    if (firstInvalid) firstInvalid.focus();
    return allValid;
  }

  function bindValidation(root) {
    const form = root.querySelector('.form-builder__form');
    if (!form) return;
    const strings = getStrings(root);

    // Submit is handled by bindAjaxSubmit / bindRecaptcha (always preventDefault).

    const liveFields = form.querySelectorAll(
      'input:not([type="hidden"]):not([type="submit"]), textarea, select'
    );
    liveFields.forEach((field) => {
      const event =
        field.type === 'checkbox' || field.type === 'radio' || field.type === 'file'
          ? 'change'
          : 'blur';
      field.addEventListener(event, () => validateField(field, strings));
    });
  }

  function onFileChange(input) {
    const nameEl = input.closest('.form-builder__file')?.querySelector('[data-form-builder-file-name]');
    const file = input.files && input.files[0];

    if (!nameEl) return;

    if (!file) {
      nameEl.textContent = '';
      return;
    }

    nameEl.textContent = file.name;
  }

  function bindFileInputs(root) {
    const inputs = root.querySelectorAll('input[type="file"]');
    inputs.forEach((input) => {
      input.addEventListener('change', () => onFileChange(input));
    });
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function getStrings(root) {
    const key = root.dataset.formBuilderJsKey;
    if (key && window[key] && window[key].strings) {
      return window[key].strings;
    }
    return {};
  }

  function showRecaptchaError(root) {
    const msg =
      getStrings(root).recaptcha_error ||
      'Something went wrong with the security check. Please try again.';
    showFormBuilderAlert(root, msg, 'form-builder__recaptcha-error');
  }

  /**
   * @param {HTMLElement} root
   * @param {string} msg
   * @param {string} [className]
   */
  function clearFormBuilderResult(root) {
    const result = root.querySelector('[data-form-builder-result]');
    if (result) result.innerHTML = '';
  }

  function showFormBuilderAlert(root, msg, className = 'form-builder__alert') {
    if (!msg) return;
    const result = root.querySelector('[data-form-builder-result]');
    if (result) {
      result.innerHTML = `<p class="${className}" role="alert">${escapeHtml(msg)}</p>`;
      result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * Success copy from theme settings (richtext HTML) or storefront translation fallback.
   * @param {HTMLElement} root
   */
  function showFormBuilderSuccess(root) {
    const strings = getStrings(root);
    const result = root.querySelector('[data-form-builder-result]');
    if (!result) return;

    const html =
      strings.success_message ||
      strings.submit_success ||
      '<p>Thank you. Your message has been sent.</p>';

    result.innerHTML = `<div class="form-builder__success rte" role="status">${html}</div>`;
    result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function resetFormBuilderForm(form) {
    form.reset();
    form.querySelectorAll('input[type="file"]').forEach((input) => onFileChange(input));
    form.querySelectorAll('.' + FIELD_ERROR_CLASS).forEach((wrapper) => {
      wrapper.classList.remove(FIELD_ERROR_CLASS);
      const errorEl = wrapper.querySelector('.' + FIELD_ERROR_MSG_CLASS);
      if (errorEl) errorEl.textContent = '';
    });
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLFormElement} form
   */
  async function submitFormAjax(root, form) {
    clearFormBuilderResult(root);
    setSubmitButtonsLoading(form, true);

    const strings = getStrings(root);
    const recaptchaEl = form.querySelector(`.${RECAPTCHA_CLASS}`);
    const inst = recaptchaInstances.find((i) => i.form === form);

    try {
      const response = await fetch(form.action, {
        method: form.method || 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json, text/plain, */*',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      const bodyText = await response.text();

      if (response.ok) {
        resetFormBuilderForm(form);
        if (inst?.widgetId !== undefined && window.grecaptcha?.reset) {
          window.grecaptcha.reset(inst.widgetId);
        }
        showFormBuilderSuccess(root);
      } else {
        const msg =
          bodyText.trim() ||
          strings.submit_error ||
          'Something went wrong. Please try again.';
        showFormBuilderAlert(root, msg);
      }
    } catch (err) {
      console.error('[form-builder] submit failed', err);
      showFormBuilderAlert(
        root,
        strings.submit_error || 'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitButtonsLoading(form, false);
    }
  }

  function bindRecaptchaRequiredGuard(root) {
    const form = root.querySelector('.form-builder__form');
    if (!form || form.querySelector(`.${RECAPTCHA_CLASS}`)) {
      return;
    }

    const msg = getStrings(root).recaptcha_required;
    if (!msg) {
      return;
    }

    form.addEventListener('submit', function handleMissingRecaptcha(ev) {
      ev.preventDefault();
      showFormBuilderAlert(root, msg);
    });
  }

  function setSubmitButtonsLoading(form, loading) {
    form.querySelectorAll('[type="submit"]').forEach((button) => {
      if (loading) {
        if (!button.classList.contains(BUTTON_LOADING_CLASS)) {
          button.classList.add(BUTTON_LOADING_CLASS);
        }
        button.setAttribute('disabled', '');
      } else {
        button.classList.remove(BUTTON_LOADING_CLASS);
        button.removeAttribute('disabled');
      }
    });
  }

  function uniqueRecaptchaCallbackName() {
    const randomize = () => {
      const rand = Math.random().toString(36).slice(2, 7);
      return `${rand[0].toUpperCase()}${rand.slice(1)}`;
    };

    let name = `recaptchaCallbackForm${randomize()}`;
    while (recaptchaInstances.find((inst) => inst.callbackName === name)) {
      name = `recaptchaCallbackForm${randomize()}`;
    }
    return name;
  }

  function renderRecaptcha(inst) {
    const el = inst.recaptchaEl;
    if (!window.grecaptcha || typeof window.grecaptcha.render !== 'function') {
      return;
    }
    const attributes = {
      sitekey: el.dataset.sitekey,
      size: el.dataset.size || 'invisible',
      callback: el.dataset.callback,
    };
    inst.widgetId = window.grecaptcha.render(el, attributes);
  }

  function recaptchaOnLoadCallback() {
    recaptchaInstances.forEach((inst) => {
      try {
        renderRecaptcha(inst);
      } catch (e) {
        console.error('[form-builder] reCAPTCHA render failed', e);
      }
    });
    recaptchaOnLoadCallbackCalled = true;
  }

  function exposeRecaptchaOnLoadCallback() {
    if (recaptchaOnLoadCallbackExposed) {
      return;
    }
    window.onloadRecaptchaCallback = function () {
      recaptchaOnLoadCallback();
    };
    recaptchaOnLoadCallbackExposed = true;
  }

  function bindRecaptcha(root, form, recaptchaEl) {
    const callbackName = uniqueRecaptchaCallbackName();
    recaptchaEl.dataset.callback = callbackName;

    const inst = {
      form,
      root,
      recaptchaEl,
      callbackName,
    };

    window[callbackName] = function () {
      submitFormAjax(root, form);
    };

    recaptchaInstances.push(inst);
    exposeRecaptchaOnLoadCallback();

    form.addEventListener('submit', function handleSubmit(ev) {
      ev.preventDefault();

      if (!validateForm(form, getStrings(root))) {
        return;
      }

      setSubmitButtonsLoading(form, true);

      let pollCount = 0;

      function proceed() {
        if (!recaptchaOnLoadCallbackCalled) {
          pollCount += 1;
          if (pollCount > 200) {
            showRecaptchaError(root);
            setSubmitButtonsLoading(form, false);
            return;
          }
          setTimeout(proceed, 100);
          return;
        }

        const tokenInput = form.querySelector(`[name="${RECAPTCHA_RESPONSE_NAME}"]`);
        if (tokenInput && tokenInput.value !== '' && inst.widgetId !== undefined) {
          window.grecaptcha.reset(inst.widgetId);
        }

        if (inst.widgetId === undefined) {
          showRecaptchaError(root);
          setSubmitButtonsLoading(form, false);
          return;
        }

        window.grecaptcha.execute(inst.widgetId).catch(() => {
          showRecaptchaError(root);
          setSubmitButtonsLoading(form, false);
        });
      }

      proceed();
    });
  }

  function bindAjaxSubmit(root) {
    const form = root.querySelector('.form-builder__form');
    if (!form) return;

    const hasRecaptcha = !!form.querySelector(`.${RECAPTCHA_CLASS}`);
    if (hasRecaptcha) return;

    const strings = getStrings(root);

    form.addEventListener('submit', function handleAjaxSubmit(ev) {
      ev.preventDefault();
      if (!validateForm(form, strings)) return;
      submitFormAjax(root, form);
    });
  }

  function bindRecaptchaIfPresent(root) {
    const form = root.querySelector('.form-builder__form');
    const recaptchaEl = form && form.querySelector(`.${RECAPTCHA_CLASS}`);
    if (!form || !recaptchaEl || !recaptchaEl.dataset.sitekey) {
      return;
    }
    bindRecaptcha(root, form, recaptchaEl);
  }

  function init() {
    document.querySelectorAll('.form-builder[data-form-builder-root]').forEach((root) => {
      bindFileInputs(root);
      bindValidation(root);
      bindAjaxSubmit(root);
      bindRecaptchaIfPresent(root);
      bindRecaptchaRequiredGuard(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
